"use client"

import { useSearchParams } from "next/navigation"
import { useState } from "react"
import { AuthShell } from "@/components/auth/login/auth-shell"
import { CellphoneStepForm } from "@/components/auth/login/cellphone-step-form"
import { OtpStepDialog } from "@/components/auth/login/otp-step-dialog"
import { PendingRegistrationForm } from "@/components/auth/login/pending-registration-form"

type LoginStep = "cellphone" | "otp" | "registration"

const maskCellphone = (rawCellphone: string): string => {
  const digits = rawCellphone.replace(/\D/g, "")
  const normalizedDigits = digits.startsWith("55") ? digits : `55${digits}`
  const visibleSuffix = normalizedDigits.slice(-4)

  return `+${normalizedDigits.slice(0, 2)} ********${visibleSuffix}`
}

export default function Page() {
  const searchParams = useSearchParams()
  const callbackParam = searchParams.get("callback")

  const [step, setStep] = useState<LoginStep>("cellphone")
  const [cellphone, setCellphone] = useState("")
  const [transactionId, setTransactionId] = useState<string | null>(null)
  const [pendingTriiboId, setPendingTriiboId] = useState<string | null>(null)
  const [resendSeed, setResendSeed] = useState(0)

  const resetLoginFlowState = () => {
    setStep("cellphone")
    setCellphone("")
    setTransactionId(null)
    setPendingTriiboId(null)
    setResendSeed(0)
  }

  const handleOtpSent = ({
    cellphone: newCellphone,
    transactionId: newTransactionId,
  }: {
    cellphone: string
    transactionId: string
  }) => {
    setCellphone(newCellphone)
    setTransactionId(newTransactionId)
    setPendingTriiboId(null)
    setStep("otp")
    setResendSeed((prev) => prev + 1)
  }

  const handleOtpResent = (newTransactionId: string) => {
    setTransactionId(newTransactionId)
    setResendSeed((prev) => prev + 1)
  }

  const handleRegistrationRequired = (triiboId: string) => {
    setPendingTriiboId(triiboId)
    setStep("registration")
  }

  const handleAuthenticated = async () => {
    resetLoginFlowState()
  }

  return (
    <AuthShell title='Entrar/Registrar'>
      <CellphoneStepForm
        initialCellphone={cellphone}
        onOtpSent={handleOtpSent}
      />

      <OtpStepDialog
        callbackParam={callbackParam}
        cellphone={cellphone}
        cellphoneDisplay={maskCellphone(cellphone)}
        isOpen={step === "otp"}
        onAuthenticated={handleAuthenticated}
        onDismiss={() => setStep("cellphone")}
        onOtpResent={handleOtpResent}
        onRegistrationRequired={handleRegistrationRequired}
        resendSeed={resendSeed}
        transactionId={transactionId}
      />

      {step === "registration" && pendingTriiboId ? (
        <PendingRegistrationForm
          callbackParam={callbackParam}
          cellphone={cellphone}
          onAuthenticated={handleAuthenticated}
          triiboId={pendingTriiboId}
        />
      ) : null}
    </AuthShell>
  )
}
